import React, { useState, useEffect } from 'react'
import Overlay from '@ynap/overlay'
import { Button } from '@ynap/button'
import BEMHelper from '@ynap/bem-helper'
import EmptyList from '@ynap/empty-list'
import CreditsSkeleton from './CreditsSkeleton'
import type { StoreCreditProps, Transaction } from './types'

const bem = new BEMHelper('StoreCredit')
const transactionBem = new BEMHelper('TransactionItem')

const RECENT_TRANSACTIONS_NUMBER = 6

export function TransactionItem({ date, amount, credit }: Transaction) {
    return (
        <li className={transactionBem('', credit && 'highlight')}>
            <span className={transactionBem('date')}>{date}</span>
            <span className={transactionBem('amount')}>{amount}</span>
        </li>
    )
}

function StoreCredit({ credit, transactions, limit, fetchStoreCredit, storeCreditError, messages }: StoreCreditProps) {
    const [transactionsModalOpen, setTransactionsModalOpen] = useState(false)

    useEffect(() => {
        if (!(credit || transactions) && !storeCreditError) fetchStoreCredit()
    }, [])

    if (storeCreditError) {
        const noCreditsFound = storeCreditError === 'CREDITS_NOT_FOUND'
        const { title, noStoreCredit, error } = messages.credits
        const { retryNow } = messages.myAccountLanding

        return (
            <EmptyList
                className={bem('emptyState', noCreditsFound ? 'noCreditsFound' : 'error')}
                title={title()}
                variant="Credit"
            >
                <p className={bem('label')}>{noCreditsFound ? noStoreCredit() : error()}</p>
                {noCreditsFound ? null : (
                    <Button onClick={() => fetchStoreCredit()} type="primary-link" className={bem('emptyLink')}>
                        {retryNow()}
                    </Button>
                )}
            </EmptyList>
        )
    }

    const {
        credits: {
            brand,
            title,
            subheading,
            transactions: transactionsTitle,
            allTransactions,
            viewAll,
            remainingCredit,
            closeTransactions,
        },
    } = messages

    const recentTransactionsLimit = limit || RECENT_TRANSACTIONS_NUMBER

    return (
        <>
            <div className={bem('innerContainer')}>
                <h2 className={bem('title')}>{title()}</h2>
                {subheading ? <div className={bem('subtitle')}>{subheading()}</div> : null}
                {typeof transactions === 'undefined' ? (
                    <CreditsSkeleton />
                ) : (
                    <div className={bem('card')}>
                        <h3 className={bem('header')}>{brand?.() || ''}</h3>
                        <div className={bem('credit')}>
                            <span className={bem('creditAmount')}>{credit}</span>
                            <span className={bem('creditLabel')}>{remainingCredit()}</span>
                        </div>
                        <details>
                            <summary className={bem('summary')}>{transactionsTitle()}</summary>
                            <ul className={bem('recentTransactions')}>
                                {transactions.slice(0, recentTransactionsLimit).map((t, i) => (
                                    <TransactionItem {...t} key={`transaction${i}-${t.date}-${t.amount}-${t.type}`} />
                                ))}
                            </ul>
                            {transactions.length > recentTransactionsLimit ? (
                                <div
                                    role="button"
                                    className={bem('viewAll')}
                                    onClick={() => setTransactionsModalOpen(true)}
                                >
                                    {viewAll()}
                                </div>
                            ) : null}
                        </details>
                    </div>
                )}
            </div>
            {transactionsModalOpen ? (
                <Overlay
                    title={allTransactions()}
                    onClose={() => setTransactionsModalOpen(false)}
                    handleBodyClick={() => setTransactionsModalOpen(false)}
                >
                    <ul className={bem('allTransactions', closeTransactions && 'padBottom')}>
                        {transactions.map((t, i) => (
                            <TransactionItem {...t} key={`transaction${i}-${t.date}-${t.amount}-${t.type}`} />
                        ))}
                    </ul>
                    {closeTransactions ? (
                        <div className={bem('closeTransactions')}>
                            <Button
                                type="primary"
                                onClick={() => setTransactionsModalOpen(false)}
                                className={bem('closePrimaryButton')}
                            >
                                {closeTransactions()}
                            </Button>
                        </div>
                    ) : null}
                </Overlay>
            ) : null}
        </>
    )
}

export default StoreCredit
